import { FC, useEffect, useState } from "react";
import {
  Table,
  DatePicker,
  Select,
  Input,
  Button,
  notification,
  Switch,
  Modal,
  Tabs,
  Radio,
  Tooltip,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import moment from "moment";
import style from "./style.module.scss";
import API from "../../../services/api.service";
import { APIS } from "../../../constants/apis";
import DayTypeLegend from "../../DayTypeLegend";
import {
  fetchSpecialDays,
  specialDayClass,
  specialDayLabel,
  EMPTY_SPECIAL_DAYS,
  SpecialDayIndex,
} from "../../../utils/dayTypes";

const { Option } = Select;
const { RangePicker } = DatePicker;

const TYPE_META: any = {
  1: { label: "General", cls: "badge_other" },
  2: { label: "Peak", cls: "badge_peak" },
  3: { label: "Special / Holiday", cls: "badge_special" },
  4: { label: "Other / Event", cls: "badge_other" },
};

const SpecialDays: FC = () => {
  const [list, setList] = useState<any[]>([]);

  // The days already marked peak / high peak, so the pickers below show what is
  // taken before a new one is added on top. Refreshed after every save so a day
  // just added shows immediately. This screen has no villa context, so it says
  // nothing about maintenance or bookings.
  const [specialDays, setSpecialDays] =
    useState<SpecialDayIndex>(EMPTY_SPECIAL_DAYS);
  useEffect(() => {
    fetchSpecialDays().then(setSpecialDays);
  }, []);

  const dayTypeRender = (current: any) => {
    const cls = specialDayClass(current, specialDays);
    const inner = (
      <div className={`ant-picker-cell-inner ${cls}`}>{current.date()}</div>
    );
    const title = specialDayLabel(cls);
    return title ? <Tooltip title={title}>{inner}</Tooltip> : inner;
  };
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState<number | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<
    "latest" | "oldest" | "name" | "name_desc"
  >("latest");
  const [search, setSearch] = useState<string>("");

  // add form (dialog)
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [type, setType] = useState<number>(3);
  const [event, setEvent] = useState<string>("");
  const [recurring, setRecurring] = useState<boolean>(false);
  // recurring peak year range (a real peak entry is created for each year)
  const [fromYear, setFromYear] = useState<number | undefined>(undefined);
  const [toYear, setToYear] = useState<number | undefined>(undefined);

  // edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editId, setEditId] = useState<number | null>(null); // single-day holiday
  const [editPeakIds, setEditPeakIds] = useState<number[] | null>(null); // whole peak
  const [editStart, setEditStart] = useState<string>("");
  const [editEnd, setEditEnd] = useState<string>("");
  const [editType, setEditType] = useState<number>(3);
  const [editEvent, setEditEvent] = useState<string>("");
  const [editRecurring, setEditRecurring] = useState<boolean>(false);
  // Last year the recurring set should run to. The Add dialog materialises one
  // real entry per year, so changing this on edit means creating the missing
  // years or deleting the ones past the new end.
  const [editToYear, setEditToYear] = useState<number | undefined>(undefined);
  // which years an edit of a recurring event applies to
  const [editScope, setEditScope] = useState<"this" | "this_following" | "all">(
    "this"
  );

  // peak delete (per-year scope) dialog
  const [peakDeleteOpen, setPeakDeleteOpen] = useState(false);
  const [peakDeleteRow, setPeakDeleteRow] = useState<any>(null);
  const [peakDeleting, setPeakDeleting] = useState(false);
  const [peakScope, setPeakScope] = useState<string>("this");

  const isPeakEdit = editPeakIds !== null;

  const fetchList = async () => {
    try {
      setLoading(true);
      const res = await API.get(APIS.SPECIAL_DAYS.LIST);
      if (res.data.status === 1) setList(res.data.data || []);
      // the pickers shade from the same data, so refresh them together
      fetchSpecialDays().then(setSpecialDays);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, []);

  const resetAdd = () => {
    setStartDate("");
    setEndDate("");
    setType(3);
    setEvent("");
    setRecurring(false);
    setFromYear(undefined);
    setToYear(undefined);
  };

  const openAdd = () => {
    resetAdd();
    setAddOpen(true);
  };

  const onAdd = async () => {
    if (!event || !startDate || !endDate || !type) {
      notification.error({
        message: "Please enter a name, date and type",
        placement: "top",
      });
      return;
    }

    // Recurring Peak / Special day with a year range -> create a REAL entry for
    // EACH year in [fromYear, toYear]. Bounded — nothing beyond the range.
    if ((type === 2 || type === 3) && recurring) {
      const fy = fromYear || moment(startDate).year();
      const ty = toYear || fy;
      if (ty < fy) {
        notification.error({
          message: "'To year' must be on or after 'From year'",
          placement: "top",
        });
        return;
      }
      const yearSpan = moment(endDate).year() - moment(startDate).year();
      try {
        setSaving(true);
        let ok = 0;
        // A year whose dates were all skipped still comes back status:1, so
        // count the rows the API says it actually wrote.
        let addedTotal = 0;
        let skippedTotal = 0;
        let firstError = "";
        for (let y = fy; y <= ty; y++) {
          const s = moment(startDate).year(y).format("YYYY-MM-DD");
          const e = moment(endDate)
            .year(y + yearSpan)
            .format("YYYY-MM-DD");
          // eslint-disable-next-line no-await-in-loop
          // Flag every generated row as part of a recurring set. Persisting it
          // is what lets the badge and the edit/delete scope be driven by the
          // stored value instead of guessing from the event name.
          const res = await API.post(APIS.SPECIAL_DAYS.ADD, {
            start_date: s,
            end_date: e,
            type,
            event,
            recurring: 1,
          });
          if (res.data.status === 1) {
            ok++;
            addedTotal += Number(res.data.data?.added ?? 0);
            skippedTotal += Number(res.data.data?.skipped ?? 0);
          } else if (!firstError) firstError = res.data.message || "";
        }
        if (addedTotal > 0) {
          notification.success({
            message: `Added ${addedTotal} day(s) across ${ok} year${
              ok > 1 ? "s" : ""
            }${skippedTotal ? `, ${skippedTotal} already existed` : ""}`,
            placement: "top",
          });
        } else if (ok > 0) {
          notification.warning({
            message: `Nothing added — all ${skippedTotal} date${
              skippedTotal > 1 ? "s" : ""
            } already had a special day. Edit or delete the existing entries instead.`,
            placement: "top",
          });
        } else {
          notification.error({
            message: firstError || "Failed to add",
            placement: "top",
          });
        }
        resetAdd();
        setAddOpen(false);
        fetchList();
      } catch (e: any) {
        notification.error({
          message: e?.response?.data?.message || "Something went wrong",
          placement: "top",
        });
      } finally {
        setSaving(false);
      }
      return;
    }

    try {
      setSaving(true);
      const res = await API.post(APIS.SPECIAL_DAYS.ADD, {
        start_date: startDate,
        end_date: endDate,
        type,
        event,
        recurring: 0,
      });
      if (res.data.status === 1) {
        // The API skips any date that already carries a special day — whatever
        // its type — and still answers status:1 with added:0. Reporting that as
        // "added" made a silent no-op look like it had worked, so the new entry
        // never appeared on the calendars.
        const added = Number(res.data.data?.added ?? 0);
        const skipped = Number(res.data.data?.skipped ?? 0);
        if (added === 0) {
          notification.warning({
            message: skipped
              ? `Nothing added — ${skipped} date${
                  skipped > 1 ? "s" : ""
                } already had a special day. Edit or delete the existing entry instead.`
              : "Nothing was added.",
            placement: "top",
          });
        } else {
          notification.success({
            message: skipped
              ? `Added ${added} day(s), ${skipped} already existed`
              : "Special day added",
            placement: "top",
          });
        }
        resetAdd();
        setAddOpen(false);
        fetchList();
      } else {
        notification.error({
          message: res.data.message || "Failed",
          placement: "top",
        });
      }
    } catch (e: any) {
      notification.error({
        message: e?.response?.data?.message || "Something went wrong",
        placement: "top",
      });
    } finally {
      setSaving(false);
    }
  };

  // Edit a special/holiday day. Treated as its year's "period" (like a peak) so
  // it supports a date range, the recurring toggle, and the per-year scope.
  const onEdit = (row: any) => {
    const year = new Date(row.date).getFullYear();
    const ids = peakScopeIds(row.event, "this", year, row.type);
    const yearDates = (list || [])
      .filter(
        (r: any) =>
          r.type === row.type &&
          sameEvent(r.event, row.event) &&
          new Date(r.date).getFullYear() === year
      )
      .map((r: any) => moment(r.date).format("YYYY-MM-DD"))
      .sort();
    const d = moment(row.date).format("YYYY-MM-DD");
    setEditId(row.id);
    setEditPeakIds(ids.length ? ids : [row.id]);
    setEditStart(yearDates[0] || d);
    setEditEnd(yearDates[yearDates.length - 1] || d);
    setEditType(row.type);
    setEditEvent(row.event);
    setEditRecurring(Number(row.recurring) === 1);
    setEditToYear(seriesEndYear(row.event, row.type, yearDates[0] || d));
    setEditScope("this");
    setEditOpen(true);
  };

  // Edit a whole Peak period (range) -> dialog.
  const onEditPeak = (row: any) => {
    setEditId(null);
    setEditPeakIds(row.ids);
    setEditStart(moment(row.start).format("YYYY-MM-DD"));
    setEditEnd(moment(row.end).format("YYYY-MM-DD"));
    setEditType(2);
    setEditEvent(row.event);
    setEditRecurring(Number(row.recurring) === 1);
    setEditToYear(
      seriesEndYear(row.event, 2, moment(row.start).format("YYYY-MM-DD"))
    );
    setEditScope("this");
    setEditOpen(true);
  };

  const onUpdate = async () => {
    if (!editEvent.trim() || !editStart || (isPeakEdit && !editEnd)) return;
    // Peak (2) and Special/Holiday (3) can both be recurring; keep the flag
    // rather than silently clearing it when a special day is edited.
    const recur = (editType === 2 || editType === 3) && editRecurring ? 1 : 0;
    const evt = editEvent.trim();
    const { min, max } = peakYearRange(editEvent, editType);
    const isRec = max > min;
    const clickedYear = moment(editStart).year();
    // Non-recurring events (or the default "this year") only touch the one entry.
    const scope = isRec ? editScope : "this";
    try {
      setEditSaving(true);

      if (scope === "this") {
        const res = isPeakEdit
          ? await API.put(APIS.SPECIAL_DAYS.UPDATE_PERIOD, {
              ids: editPeakIds,
              start_date: editStart,
              end_date: editEnd,
              type: editType,
              event: evt,
              recurring: recur,
            })
          : await API.put(APIS.SPECIAL_DAYS.UPDATE + editId, {
              date: editStart,
              type: editType,
              event: evt,
              recurring: recur,
            });
        if (res.data.status !== 1) {
          notification.error({
            message: res.data.message || "Failed",
            placement: "top",
          });
          return;
        }
      } else {
        // Apply the edited date range (shifted to each year) + name to every year
        // in the chosen scope. Peaks update as a period; special days per day-row.
        const yearSpan = isPeakEdit
          ? moment(editEnd).year() - moment(editStart).year()
          : 0;
        const years: number[] = [];
        for (let y = min; y <= max; y++) {
          if (scope === "this_following" && y < clickedYear) continue;
          years.push(y);
        }
        let ok = 0;
        let firstErr = "";
        for (const y of years) {
          const s = moment(editStart).year(y).format("YYYY-MM-DD");
          const e = moment(editEnd)
            .year(y + yearSpan)
            .format("YYYY-MM-DD");
          const ids = peakScopeIds(editEvent, "this", y, editType);
          if (!ids.length) continue;
          if (isPeakEdit) {
            // eslint-disable-next-line no-await-in-loop
            const r = await API.put(APIS.SPECIAL_DAYS.UPDATE_PERIOD, {
              ids,
              start_date: s,
              end_date: e,
              type: editType,
              event: evt,
              recurring: recur,
            });
            if (r.data.status === 1) ok++;
            else if (!firstErr) firstErr = r.data.message || "";
          } else {
            for (const id of ids) {
              // eslint-disable-next-line no-await-in-loop
              const r = await API.put(APIS.SPECIAL_DAYS.UPDATE + id, {
                date: s,
                type: editType,
                event: evt,
                recurring: recur,
              });
              if (r.data.status === 1) ok++;
              else if (!firstErr) firstErr = r.data.message || "";
            }
          }
        }
        if (ok === 0) {
          notification.error({
            message: firstErr || "Failed to update",
            placement: "top",
          });
          return;
        }
      }

      // Grow or shrink the series to the chosen end year. A recurring set is
      // real rows — one per year — so this creates the missing years or deletes
      // the ones past the new end. Runs after the edit above so the years it
      // copies forward carry the edited name / dates / type.
      let spanNote = "";
      if (recur === 1 && editToYear) {
        const baseYear = moment(editStart).year();
        const currentEnd = max > baseYear ? max : baseYear;
        const yearSpan = isPeakEdit
          ? moment(editEnd).year() - moment(editStart).year()
          : 0;

        if (editToYear > currentEnd) {
          let added = 0;
          for (let y = currentEnd + 1; y <= editToYear; y++) {
            const s2 = moment(editStart).year(y).format("YYYY-MM-DD");
            const e2 = moment(editEnd || editStart)
              .year(y + yearSpan)
              .format("YYYY-MM-DD");
            // eslint-disable-next-line no-await-in-loop
            const r = await API.post(APIS.SPECIAL_DAYS.ADD, {
              start_date: s2,
              end_date: e2,
              type: editType,
              event: evt,
              recurring: 1,
            });
            if (r.data.status === 1) added++;
          }
          if (added) spanNote = ` — extended to ${editToYear}`;
        } else if (editToYear < currentEnd) {
          const drop = (list || [])
            .filter(
              (r: any) =>
                r.type === editType &&
                sameEvent(r.event, evt) &&
                Number(r.recurring) === 1 &&
                new Date(r.date).getFullYear() > editToYear
            )
            .map((r: any) => r.id);
          if (drop.length) {
            await API.post(APIS.SPECIAL_DAYS.DELETE_PERIOD, { ids: drop });
            spanNote = ` — now ends ${editToYear}`;
          }
        }
      }

      notification.success({
        message: `Updated${spanNote}`,
        placement: "top",
      });
      setEditOpen(false);
      fetchList();
    } catch (e: any) {
      notification.error({
        message: e?.response?.data?.message || "Something went wrong",
        placement: "top",
      });
    } finally {
      setEditSaving(false);
    }
  };

  const onDelete = async (row: any) => {
    try {
      const res = await API.delete(APIS.SPECIAL_DAYS.DELETE + row.id);
      if (res.data.status === 1) {
        notification.success({
          message: "Special day deleted",
          placement: "top",
        });
        fetchList();
      } else {
        notification.error({
          message: res.data.message || "Failed",
          placement: "top",
        });
      }
    } catch (e) {
      notification.error({ message: "Something went wrong", placement: "top" });
    }
  };

  // A Peak "event" can repeat across years (one day-row set per year). Scopes are
  // relative to the year of the row that was clicked (`clickedYear`):
  //   this            -> that year only
  //   this_following  -> that year and all later years
  //   all             -> every year
  // Same peak "event" regardless of casing / surrounding spaces, so
  // "Summer holiday" and "Summer Holiday" count as one recurring peak.
  const sameEvent = (a: any, b: any) =>
    String(a || "").trim().toLowerCase() ===
    String(b || "").trim().toLowerCase();

  // Works for both Peak (type 2) and Special/Holiday (type 3) — a recurring entry
  // of either kind is stored as one set of day-rows per year, keyed by event name.
  const peakScopeIds = (
    event: string,
    scope: string,
    clickedYear: number,
    type = 2
  ): number[] => {
    return (list || [])
      .filter(
        (r: any) =>
          r.type === type &&
          sameEvent(r.event, event) &&
          // The recurring flag only guards the MULTI-YEAR scopes: it is what stops
          // a sweep across years from catching an unrelated one-off that happens to
          // share a name. Within a single year the year itself is that guard, and
          // demanding the flag here returned no ids at all for a non-recurring
          // period — so a one-off peak could be neither deleted nor edited.
          (scope === "this" || Number(r.recurring) === 1)
      )
      .filter((r: any) => {
        const y = new Date(r.date).getFullYear();
        if (scope === "this") return y === clickedYear;
        if (scope === "this_following") return y >= clickedYear;
        return true; // all
      })
      .map((r: any) => r.id);
  };

  // Min / max year this event spans (used to decide which options to show).
  const peakYearRange = (event: string, type = 2) => {
    // Only rows saved as part of a recurring set span a range; two one-off
    // events sharing a name are unrelated and must not be grouped.
    const ys = (list || [])
      .filter(
        (r: any) =>
          r.type === type &&
          sameEvent(r.event, event) &&
          Number(r.recurring) === 1
      )
      .map((r: any) => new Date(r.date).getFullYear());
    return ys.length
      ? { min: Math.min(...ys), max: Math.max(...ys) }
      : { min: 0, max: 0 };
  };

  // Last year the set currently runs to. Falls back to the edited entry's own
  // year for a one-off, so switching Recurring on starts the series from there.
  const seriesEndYear = (event: string, type: number, startDate: string) => {
    const base = startDate ? moment(startDate).year() : moment().year();
    const { max } = peakYearRange(event, type);
    return max > base ? max : base;
  };

  const peakClickedYear = (row: any) =>
    row ? new Date(row.start || row.date).getFullYear() : 0;

  // A row is recurring only when it was SAVED as part of a recurring set.
  // Inferring it from "same event name in more than one year" made two unrelated
  // events that happen to share a name (e.g. both called "event") show the badge
  // and, worse, let a delete scoped to "all years" sweep up the other one.
  const isRecurring = (row: any) => Number(row.recurring) === 1;

  const deletePeakScope = async (scope: string) => {
    if (!peakDeleteRow) return;
    const isPeak = peakDeleteRow.type === 2;
    const ids = peakScopeIds(
      peakDeleteRow.event,
      scope,
      peakClickedYear(peakDeleteRow),
      peakDeleteRow.type
    );
    if (!ids.length) {
      notification.info({
        message: `No ${isPeak ? "peak" : "special"} days to delete for that range`,
        placement: "top",
      });
      return;
    }
    try {
      setPeakDeleting(true);
      const res = await API.post(APIS.SPECIAL_DAYS.DELETE_PERIOD, { ids });
      if (res.data.status === 1) {
        notification.success({
          message: isPeak ? "Peak period deleted" : "Special days deleted",
          placement: "top",
        });
        setPeakDeleteOpen(false);
        setPeakDeleteRow(null);
        fetchList();
      } else {
        notification.error({
          message: res.data.message || "Failed",
          placement: "top",
        });
      }
    } catch (e) {
      notification.error({ message: "Something went wrong", placement: "top" });
    } finally {
      setPeakDeleting(false);
    }
  };

  // Peak periods, and Special/Holiday days that repeat across years, open the
  // per-year scope dialog (This year / following / all). A one-off special day
  // (single year, not flagged recurring) uses a plain confirm.
  const confirmDelete = (row: any) => {
    if (row._kind === "peak") {
      setPeakDeleteRow(row);
      setPeakScope("this");
      setPeakDeleteOpen(true);
      return;
    }
    if (row.type === 3) {
      if (isRecurring(row)) {
        setPeakDeleteRow(row);
        setPeakScope("this");
        setPeakDeleteOpen(true);
        return;
      }
    }
    Modal.confirm({
      title: "Are you sure you want to delete this special day?",
      okText: "Delete",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: () => onDelete(row),
    });
  };

  const columns = [
    { title: "Name", dataIndex: "name", key: "name" },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (d: string, row: any) =>
        row._kind === "peak"
          ? `${moment(row.start).format("DD MMM YYYY")} – ${moment(
              row.end
            ).format("DD MMM YYYY")}`
          : moment(d).format("DD MMM YYYY"),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (t: number, row: any) => {
        const meta = TYPE_META[t] || { label: t, cls: "badge_other" };
        return (
          <span className={style.type_cell}>
            <span className={`${style.badge_type} ${style[meta.cls]}`}>
              {meta.label}
            </span>
            {isRecurring(row) && (
              <span className={style.badge_recurring}>↻ Recurring</span>
            )}
          </span>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_: any, row: any) => (
        <span className={style.row_actions}>
          {row._kind === "peak" ? (
            <EditOutlined
              className={style.icon}
              onClick={() => onEditPeak(row)}
            />
          ) : (
            <EditOutlined className={style.icon} onClick={() => onEdit(row)} />
          )}
          <DeleteOutlined
            className={`${style.icon} ${style.danger}`}
            onClick={() => confirmDelete(row)}
          />
        </span>
      ),
    },
  ];

  // holidays (special days), mapped into the row shape the table/grouping use
  const combined = [
    ...list.map((r: any) => ({
      _key: `h-${r.id}`,
      _kind: "holiday",
      id: r.id,
      name: r.event,
      event: r.event,
      date: r.date,
      type: r.type,
      recurring: r.recurring,
    })),
  ];

  // Collapse Peak (type 2) day-rows into one "period" row per event + year, so a
  // peak shows / edits / deletes as a single month-to-month range instead of
  // one row per day. Everything else stays as individual rows.
  const peakGroups: any = {};
  const others: any[] = [];
  combined.forEach((r: any) => {
    if (r.type === 2) {
      const key = `${r.event}__${new Date(r.date).getFullYear()}`;
      if (!peakGroups[key]) {
        peakGroups[key] = {
          _key: `peak-${key}`,
          _kind: "peak",
          type: 2,
          name: r.event,
          event: r.event,
          recurring: r.recurring,
          ids: [],
          start: r.date,
          end: r.date,
        };
      }
      const g = peakGroups[key];
      g.ids.push(r.id);
      if (new Date(r.date) < new Date(g.start)) g.start = r.date;
      if (new Date(r.date) > new Date(g.end)) g.end = r.date;
      if (Number(r.recurring) === 1) g.recurring = 1;
    } else {
      others.push(r);
    }
  });
  const grouped = [...Object.values(peakGroups), ...others];
  // "Latest" / "Oldest" order by when the entry was CREATED, not by the event
  // date. Rows are auto-increment ids, so a higher id == more recently added.
  // A Peak period is many day-rows created together; use its newest id.
  const createdRank = (r: any) =>
    r._kind === "peak" && r.ids?.length ? Math.max(...r.ids) : r.id;
  const sorted = grouped.sort((a: any, b: any) => {
    if (sortOrder === "name") {
      return String(a.name || "").localeCompare(String(b.name || ""));
    }
    if (sortOrder === "name_desc") {
      return String(b.name || "").localeCompare(String(a.name || ""));
    }
    const ra = createdRank(a);
    const rb = createdRank(b);
    return sortOrder === "latest" ? rb - ra : ra - rb;
  });
  const q = search.trim().toLowerCase();
  const displayRows = sorted.filter((r: any) => {
    if (filterType && r.type !== filterType) return false;
    if (q && !String(r.name || "").toLowerCase().includes(q)) return false;
    return true;
  });

  const editDisabled =
    !editEvent.trim() || !editStart || (isPeakEdit && !editEnd);

  const addDisabled = !event.trim() || !startDate || !endDate;

  return (
    <div className={style.sd_wrap}>
      {/* type tabs + sort + add */}
      <Tabs
        activeKey={filterType ? String(filterType) : "all"}
        onChange={(k) => setFilterType(k === "all" ? undefined : Number(k))}
        tabBarExtraContent={
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <Input
              allowClear
              placeholder="Search by name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              prefix={<SearchOutlined style={{ color: "#9aa5a0" }} />}
              style={{ width: 220 }}
            />
            <Select
              value={sortOrder}
              onChange={(v: any) => setSortOrder(v)}
              style={{ width: 170 }}
            >
              <Option value="latest">Sort: Latest</Option>
              <Option value="oldest">Sort: Oldest</Option>
              <Option value="name">Sort: Name (A–Z)</Option>
              <Option value="name_desc">Sort: Name (Z–A)</Option>
            </Select>
            <Button className={style.add_btn} onClick={openAdd}>
              Add Event
            </Button>
          </div>
        }
      >
        <Tabs.TabPane tab="All" key="all" />
        <Tabs.TabPane tab="Peak" key="2" />
        <Tabs.TabPane tab="Special / Holiday" key="3" />
      </Tabs>
    
  
      {/* table */}
      <div className={style.table_wrap}>
        <Table
          rowKey="_key"
          columns={columns}
          dataSource={displayRows}
          loading={loading}
          pagination={{ pageSize: 10 }}
          size="middle"
        />
      </div>

      {/* add dialog */}
      <Modal
        title={null}
        footer={null}
        visible={addOpen}
        onCancel={() => setAddOpen(false)}
        destroyOnClose
      >
        <div className={style.dialog_title}>Add Event</div>
        <div className={style.field}>
          <label className={style.label}>Name</label>
          <Input
            placeholder="e.g. Diwali"
            value={event}
            onChange={(e) => setEvent(e.target.value)}
          />
        </div>

        <div className={style.field} style={{ marginTop: 12 }}>
          <label className={style.label}>Date range</label>
          <DayTypeLegend showMaintenance={false} showBooked={false} />
          <RangePicker
            style={{ width: "100%" }}
            dropdownClassName="range_fit_pop"
            value={[
              startDate ? moment(startDate) : null,
              endDate ? moment(endDate) : null,
            ]}
            onChange={(_: any, ds: any) => {
              setStartDate(ds?.[0] || "");
              setEndDate(ds?.[1] || "");
            }}
            // A peak or special day in the past can't affect any booking, so
            // there's nothing to add. Today stays selectable.
            disabledDate={(current: any) =>
              !!current && current < moment().startOf("day")
            }
            format="YYYY-MM-DD"
            getPopupContainer={(trigger: any) => trigger.parentElement}
            dateRender={dayTypeRender}
          />
        </div>

        <div className={style.field} style={{ marginTop: 12 }}>
          <label className={style.label}>Type</label>
          <Select
            style={{ width: "100%" }}
            value={type}
            onChange={(v) => setType(v)}
          >
            <Option value={3}>Special / Holiday</Option>
            <Option value={2}>Peak</Option>
          </Select>
        </div>

        {(type === 2 || type === 3) && (
          <div className={style.recur_card}>
            <div className={style.recur_head}>
              <div className={style.recur_head_left}>
                <span className={style.recur_icon}>
                  <SyncOutlined />
                </span>
                <span className={style.recur_title}>Recurring</span>
              </div>
              {recurring && (
                <div className={style.recur_ends}>
                  <span className={style.recur_ends_label}>Ends:</span>
                  <Select
                    className={style.recur_select}
                    dropdownClassName={style.recur_dropdown}
                    listHeight={232}
                    style={{ width: 100 }}
                    value={toYear}
                    placeholder="Year"
                    onChange={(v) => setToYear(Number(v))}
                  >
                    {(() => {
                      const base = startDate
                        ? moment(startDate).year()
                        : moment().year();
                      const years = [];
                      for (let y = base + 1; y <= base + 20; y++) years.push(y);
                      return years.map((y) => (
                        <Option key={y} value={y} title="">
                          {y}
                        </Option>
                      ));
                    })()}
                  </Select>
                </div>
              )}
            </div>
            <div
              className={style.switch_row}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginTop: 16,
              }}
            >
              <Switch
                checked={recurring}
                onChange={(v) => {
                  setRecurring(v);
                  const y = startDate
                    ? moment(startDate).year()
                    : moment().year();
                  setToYear(v ? y + 4 : undefined);
                }}
              />
              <span className={style.switch_hint}>Repeat every year</span>
            </div>
          </div>
        )}

        <div className={style.dialog_footer}>
          <Button
            className={style.dialog_cancel_btn}
            onClick={() => setAddOpen(false)}
          >
            Cancel
          </Button>
          <Button
            className={style.add_btn}
            loading={saving}
            disabled={addDisabled}
            onClick={onAdd}
          >
            Add
          </Button>
        </div>
      </Modal>

      {/* edit dialog */}
      <Modal
        title={null}
        footer={null}
        visible={editOpen}
        onCancel={() => setEditOpen(false)}
        destroyOnClose
      >
        <div className={style.dialog_title}>
          {editType === 2 ? "Edit peak period" : "Edit special day"}
        </div>
        <div className={style.field}>
          <label className={style.label}>Name</label>
          <Input
            placeholder="e.g. Diwali"
            value={editEvent}
            onChange={(e) => setEditEvent(e.target.value)}
          />
        </div>

        <div className={style.field} style={{ marginTop: 12 }}>
          <label className={style.label}>
            {isPeakEdit ? "Date range" : "Date"}
          </label>
          <DayTypeLegend showMaintenance={false} showBooked={false} />
          {isPeakEdit ? (
            <RangePicker
              style={{ width: "100%" }}
              dropdownClassName="range_fit_pop"
              value={[
                editStart ? moment(editStart) : null,
                editEnd ? moment(editEnd) : null,
              ]}
              onChange={(_: any, ds: any) => {
                setEditStart(ds?.[0] || "");
                setEditEnd(ds?.[1] || "");
              }}
              // Same rule as Add: a peak/special day can't be moved into the
              // past. An entry that ALREADY sits in the past keeps its dates —
              // they stay shown so name/type can still be corrected — it just
              // can't be re-pointed backwards.
              disabledDate={(current: any) =>
                !!current && current < moment().startOf("day")
              }
              format="YYYY-MM-DD"
              getPopupContainer={(trigger: any) => trigger.parentElement}
              dateRender={dayTypeRender}
            />
          ) : (
            <DatePicker
              style={{ width: "100%" }}
              getPopupContainer={(trigger: any) => trigger.parentElement}
              value={editStart ? moment(editStart) : null}
              onChange={(_, ds) => {
                setEditStart(ds);
                setEditEnd(ds);
              }}
              // Same rule as Add: a peak/special day can't be moved into the
              // past. An entry that ALREADY sits in the past keeps its dates —
              // they stay shown so name/type can still be corrected — it just
              // can't be re-pointed backwards.
              disabledDate={(current: any) =>
                !!current && current < moment().startOf("day")
              }
              format="YYYY-MM-DD"
              dateRender={dayTypeRender}
            />
          )}
        </div>

        <div className={style.field} style={{ marginTop: 12 }}>
          <label className={style.label}>Type</label>
          <Select
            style={{ width: "100%" }}
            value={editType}
            onChange={(v) => setEditType(v)}
          >
            <Option value={3}>Special / Holiday</Option>
            <Option value={2}>Peak</Option>
          </Select>
        </div>

        <div className={style.field} style={{ marginTop: 12 }}>
          <label className={style.label}>Recurring</label>
          <div
            className={style.switch_row}
            style={{ display: "flex", alignItems: "center", gap: 12 }}
          >
            <Switch
              checked={editRecurring}
              onChange={(v) => {
                setEditRecurring(v);
                // Same default as Add: a freshly-enabled series runs five years,
                // but never pull an existing set's end year backwards.
                setEditToYear(
                  v
                    ? Math.max(
                        seriesEndYear(editEvent, editType, editStart),
                        (editStart ? moment(editStart).year() : moment().year()) + 4
                      )
                    : undefined
                );
              }}
            />
            <span className={style.switch_hint}>Repeat every year</span>
            {editRecurring && (
              <div className={style.recur_ends} style={{ marginLeft: "auto" }}>
                <span className={style.recur_ends_label}>Ends:</span>
                <Select
                  className={style.recur_select}
                  dropdownClassName={style.recur_dropdown}
                  listHeight={232}
                  style={{ width: 100 }}
                  value={editToYear}
                  placeholder="Year"
                  getPopupContainer={(trigger: any) => trigger.parentElement}
                  onChange={(v) => setEditToYear(Number(v))}
                >
                  {(() => {
                    const base = editStart
                      ? moment(editStart).year()
                      : moment().year();
                    const years = [];
                    for (let y = base + 1; y <= base + 20; y++) years.push(y);
                    return years.map((y) => (
                      <Option key={y} value={y} title="">
                        {y}
                      </Option>
                    ));
                  })()}
                </Select>
              </div>
            )}
          </div>
        </div>

        {(() => {
          // Recurring event -> let the admin choose which years the edit applies to.
          const { min, max } = peakYearRange(editEvent, editType);
          if (max <= min) return null;
          const year = editStart ? moment(editStart).year() : min;
          const showFollowing = year > min && year < max;
          return (
            <div className={style.field} style={{ marginTop: 12 }}>
              <label className={style.label}>Apply changes to</label>
              <Radio.Group
                value={editScope}
                onChange={(e) => setEditScope(e.target.value)}
                style={{ display: "flex", flexDirection: "column", gap: 10 }}
              >
                <Radio value="this">This year ({year})</Radio>
                {showFollowing && (
                  <Radio value="this_following">
                    This and following years ({year}–{max})
                  </Radio>
                )}
                <Radio value="all">
                  All years ({min}–{max})
                </Radio>
              </Radio.Group>
            </div>
          );
        })()}

        <div className={style.dialog_footer}>
          <Button
            className={style.dialog_cancel_btn}
            onClick={() => setEditOpen(false)}
          >
            Cancel
          </Button>
          <Button
            className={style.add_btn}
            loading={editSaving}
            disabled={editDisabled}
            onClick={onUpdate}
          >
            Update
          </Button>
        </div>
      </Modal>

      {/* peak delete — choose which years to remove */}
      <Modal
        title={null}
        footer={null}
        visible={peakDeleteOpen}
        onCancel={() => setPeakDeleteOpen(false)}
        destroyOnClose
      >
        <div className={style.dialog_title}>
          {peakDeleteRow?.type === 3
            ? "Delete recurring special day"
            : "Delete recurring peak"}
        </div>
        {(() => {
          const year = peakClickedYear(peakDeleteRow);
          const { min, max } = peakDeleteRow
            ? peakYearRange(peakDeleteRow.event, peakDeleteRow.type)
            : { min: 0, max: 0 };
          // "This and following years" only makes sense for a middle year:
          //   at the first year it equals "All years", at the last it equals "This year".
          const showFollowing = year > min && year < max;
          // "All years" only when the peak spans more than one year.
          const showAll = max > min;
          // Same name stored under the OTHER type (e.g. a "summer" Peak vs a
          // "summer" Special day) — those are NOT touched here; call it out.
          const otherKind = (list || []).filter(
            (r: any) =>
              peakDeleteRow &&
              r.type !== peakDeleteRow.type &&
              sameEvent(r.event, peakDeleteRow.event)
          ).length;
          return (
            <>
              <Radio.Group
                value={peakScope}
                onChange={(e) => setPeakScope(e.target.value)}
                style={{ display: "flex", flexDirection: "column", gap: 16 }}
              >
                <Radio value="this">This year</Radio>
                {showFollowing && (
                  <Radio value="this_following">
                    This and following years
                  </Radio>
                )}
                {showAll && (
                  <Radio value="all">
                    All years {min}–{max}
                  </Radio>
                )}
              </Radio.Group>
              {otherKind > 0 && (
                <div
                  style={{
                    marginTop: 14,
                    fontSize: 12.5,
                    color: "#8a6d3b",
                    background: "#fcf6e6",
                    border: "1px solid #f0e2b8",
                    borderRadius: 4,
                    padding: "8px 10px",
                    lineHeight: 1.4,
                  }}
                >
                  Note: “{peakDeleteRow?.event}” also exists as{" "}
                  {peakDeleteRow?.type === 3 ? "a Peak" : "a Special / Holiday"}.
                  That is a different type and won’t be removed here — delete it
                  from its own row.
                </div>
              )}
            </>
          );
        })()}
        <div className={style.dialog_footer}>
          <Button
            className={style.dialog_cancel_btn}
            onClick={() => setPeakDeleteOpen(false)}
          >
            Cancel
          </Button>
          <Button
            className={style.add_btn}
            loading={peakDeleting}
            onClick={() => deletePeakScope(peakScope)}
          >
            OK
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default SpecialDays;
